/**
 * RSS/Atom feed URL detection and utilities
 */

import { REEDLE_PY_SERVER_URL } from './videoUtils';

/**
 * Check if a URL looks like an RSS/Atom feed URL
 * @param {string} url - URL to check
 * @returns {boolean} True if the URL appears to be an RSS/Atom feed
 */
export function isRssUrl(url) {
  if (!url) return false;

  const trimmedUrl = url.trim().toLowerCase();

  // Common RSS/Atom URL patterns
  const patterns = [
    /\/feed\/?$/,           // /feed or /feed/
    /\/rss\/?$/,            // /rss or /rss/
    /\/atom\/?$/,           // /atom or /atom/
    /\.xml$/,               // ends with .xml
    /\/feed\.xml/,          // /feed.xml
    /\/rss\.xml/,           // /rss.xml
    /\/atom\.xml/,          // /atom.xml
    /\/index\.xml/,         // /index.xml (Hugo default)
    /\?feed=/,              // ?feed= (WordPress)
    /\/feeds\//,            // /feeds/ (common path)
    /\/rss2\//,             // /rss2/ (alternative RSS)
    /feed\.rss/,            // feed.rss
    /\.rss$/,               // ends with .rss
    /\.atom$/,              // ends with .atom
  ];

  return patterns.some((pattern) => pattern.test(trimmedUrl));
}

/**
 * Fetch and parse an RSS/Atom feed via the Python server
 * @param {string} url - Feed URL to parse
 * @returns {Promise<{success: boolean, feed?: object, items?: array, total?: number, error?: string}>}
 */
export async function fetchRssFeed(url) {
  try {
    const response = await fetch(`${REEDLE_PY_SERVER_URL}/api/rss/parse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[RSS] Fetch error:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch RSS feed',
    };
  }
}
